import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Star, MapPin, ShieldCheck, BadgeCheck, Sparkles,
  Filter, X, Loader2, Video, Home, ArrowRight, CreditCard, Heart,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { Footer } from '../components/brand/Footer';
import { TrustBadge } from '../components/brand/TrustBadge';
import { SpecialtyBadgeRow } from '../components/brand/SpecialtyBadge';
import { MarketplaceDisclaimer } from '../components/brand/MarketplaceDisclaimer';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LICENSE_OPTIONS = ['Any', 'MD', 'NP', 'PA', 'RN', 'LPN', 'CNA', 'MSW', 'PT', 'OT'];
const VISIT_TYPES = [
  { value: 'any', label: 'All visit types' },
  { value: 'telehealth', label: 'Telehealth' },
  { value: 'in_person', label: 'In-home' },
];

export default function MarketplacePage() {
  const [clinicians, setClinicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [licenseType, setLicenseType] = useState('Any');
  const [visitType, setVisitType] = useState('any');
  const [maxRate, setMaxRate] = useState('');
  const [availableNow, setAvailableNow] = useState(false);
  const [payers, setPayers] = useState([]);
  const [payerId, setPayerId] = useState('any');

  const fetchClinicians = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (licenseType && licenseType !== 'Any') params.license_type = licenseType;
      if (visitType && visitType !== 'any') params.visit_type = visitType;
      if (maxRate) params.max_rate = Number(maxRate);
      if (availableNow) params.available_now = true;
      if (payerId && payerId !== 'any') params.payer_id = payerId;
      const { data } = await axios.get(`${API}/public/clinicians`, { params });
      setClinicians(data);
    } catch (e) {
      setClinicians([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClinicians(); }, []);
  useEffect(() => {
    axios.get(`${API}/insurance/payers`).then((r) => setPayers(r.data.payers || [])).catch(() => {});
  }, []);
  const submitSearch = (e) => { e.preventDefault(); fetchClinicians(); };

  // ---- Save / shortlist clinicians (patient only) ----
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [savedIds, setSavedIds] = useState(new Set());
  const isPatient = user?.role === 'patient';

  const loadSaved = useCallback(async () => {
    if (!isPatient) { setSavedIds(new Set()); return; }
    try {
      const { data } = await axios.get(`${API}/patient/saved/ids`, getAuthHeader());
      setSavedIds(new Set(data.clinician_ids || []));
    } catch (e) { /* ignore */ }
  }, [isPatient, getAuthHeader]);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  const toggleSave = async (clinicianId) => {
    if (!isPatient) {
      toast.info('Sign in as a family member to save clinicians');
      navigate('/auth?role=patient');
      return;
    }
    // optimistic
    setSavedIds((prev) => {
      const next = new Set(prev);
      next.has(clinicianId) ? next.delete(clinicianId) : next.add(clinicianId);
      return next;
    });
    try {
      const { data } = await axios.post(`${API}/patient/saved/${clinicianId}`, {}, getAuthHeader());
      toast.success(data.saved ? 'Saved to your shortlist' : 'Removed from your shortlist');
    } catch (e) {
      toast.error('Could not update your shortlist');
      loadSaved(); // resync on failure
    }
  };

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="marketplace-page">
      <Seo
        title="Find a Clinician | Browse Verified Home Care Providers | CaraLoom"
        description="Browse verified, background-checked nurses, home health aides, and caregivers near you. Compare credentials, reviews, and transparent rates, then book directly on CaraLoom."
        path="/marketplace"
        keywords={['find a clinician', 'home care providers near me', 'hire a nurse', 'hire a caregiver']}
        schema={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Find a clinician', path: '/marketplace' }])]}
      />
      <PublicNavbar />

      {/* Hero */}
      <section className="border-b border-cn-border bg-white dark:bg-cn-ink-900">
        <div className="cn-container py-14">
          <div className="cn-eyebrow mb-3">Care marketplace</div>
          <h1 className="cn-display text-4xl md:text-5xl lg:text-6xl text-cn-text max-w-3xl">
            Browse verified clinicians.<br />
            <span className="text-cn-primary italic font-medium">Book in minutes.</span>
          </h1>
          <p className="mt-5 text-cn-text-muted text-lg max-w-2xl">
            Every clinician on CaraLoom is identity-verified, license-monitored, and
            independently rated by the families they serve.
          </p>

          {/* Search bar */}
          <form onSubmit={submitSearch} className="mt-8 flex flex-col lg:flex-row gap-3 max-w-4xl">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cn-text-muted" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, specialty, city…"
                className="pl-12 h-14 rounded-2xl border-cn-border bg-white dark:bg-cn-ink-800 text-base"
                data-testid="marketplace-search-input"
              />
            </div>
            <button type="submit" className="cn-btn cn-btn-primary h-14 px-8" data-testid="marketplace-search-button">
              <Search className="w-4 h-4" /> Search
            </button>
          </form>

          {/* Filters */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-cn-text-muted" />
            <div className="w-40">
              <Select value={licenseType} onValueChange={(v) => { setLicenseType(v); }}>
                <SelectTrigger className="h-10 rounded-full border-cn-border bg-white dark:bg-cn-ink-800 text-sm" data-testid="filter-license">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LICENSE_OPTIONS.map((l) => <SelectItem key={l} value={l}>{l === 'Any' ? 'All licenses' : l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <Select value={visitType} onValueChange={(v) => { setVisitType(v); }}>
                <SelectTrigger className="h-10 rounded-full border-cn-border bg-white dark:bg-cn-ink-800 text-sm" data-testid="filter-visit-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VISIT_TYPES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={payerId} onValueChange={(v) => { setPayerId(v); }}>
                <SelectTrigger className="h-10 rounded-full border-cn-border bg-white dark:bg-cn-ink-800 text-sm" data-testid="filter-payer">
                  <SelectValue placeholder="Insurance" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All insurance</SelectItem>
                  {payers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input
              type="number"
              placeholder="Max $/hr"
              value={maxRate}
              onChange={(e) => setMaxRate(e.target.value)}
              className="h-10 w-32 rounded-full border-cn-border bg-white dark:bg-cn-ink-800 text-sm"
              data-testid="filter-max-rate"
            />
            <button
              type="button"
              onClick={() => { setAvailableNow(!availableNow); }}
              className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-medium border transition-colors ${
                availableNow ? 'bg-cn-primary text-white border-cn-primary' : 'bg-white dark:bg-cn-ink-800 text-cn-text border-cn-border hover:bg-cn-bone-100 dark:hover:bg-cn-ink-700'
              }`}
              data-testid="filter-available-now"
            >
              <span className={`w-2 h-2 rounded-full ${availableNow ? 'bg-emerald-300' : 'bg-cn-secondary'}`} />
              Available now
            </button>
            <button type="button" onClick={fetchClinicians} className="cn-btn cn-btn-ghost h-10 text-sm">Apply</button>
            {(search || licenseType !== 'Any' || visitType !== 'any' || maxRate || availableNow || payerId !== 'any') && (
              <button
                type="button"
                onClick={() => { setSearch(''); setLicenseType('Any'); setVisitType('any'); setMaxRate(''); setAvailableNow(false); setPayerId('any'); setTimeout(fetchClinicians, 0); }}
                className="text-xs text-cn-text-muted hover:text-cn-primary inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="cn-container py-12">
        <div className="flex items-end justify-between mb-6">
          <p className="text-sm text-cn-text-muted">
            {loading ? 'Loading…' : `${clinicians.length} clinician${clinicians.length !== 1 ? 's' : ''} ready to help`}
          </p>
          <Link to="/auth" className="text-sm font-medium text-cn-primary hover:underline">Get personalized AI matches →</Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-8 h-8 text-cn-primary animate-spin" />
          </div>
        ) : clinicians.length === 0 ? (
          <div className="cn-card p-12 text-center" data-testid="marketplace-empty">
            <p className="text-cn-text-muted">No clinicians match your filters. Try widening your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {clinicians.map((c) => (
              <ClinicianCard key={c.id} clinician={c} isSaved={savedIds.has(c.id)} onToggleSave={toggleSave} />
            ))}
          </div>
        )}

        <div className="mt-14">
          <MarketplaceDisclaimer variant="banner" />
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ClinicianCard({ clinician, isSaved, onToggleSave }) {
  const initials = (clinician.name || 'C').split(' ').map((n) => n[0]).join('').slice(0, 2);
  const statusColor = {
    available: 'bg-emerald-500',
    busy: 'bg-amber-500',
    offline: 'bg-cn-text-muted',
  }[clinician.availability_status] || 'bg-cn-secondary';

  return (
    <Link
      to={`/clinicians/${clinician.id}`}
      className="cn-card cn-card-hover overflow-hidden flex flex-col group"
      data-testid={`clinician-card-${clinician.id}`}
    >
      <div className="relative aspect-[4/3] bg-cn-bone-200 overflow-hidden">
        {clinician.photo_url ? (
          <img src={clinician.photo_url} alt={clinician.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cn-sage-200 to-cn-sage-400 text-white text-5xl font-display font-bold">
            {initials}
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-1.5">
          {clinician.license_verified && <TrustBadge variant="verified" label="License" />}
          {['passed', 'cleared'].includes(clinician.background_check_status) && <TrustBadge variant="background-checked" label="BG checked" />}
        </div>
        <div className="absolute top-3 right-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1">
          {clinician.review_count > 0 && clinician.rating != null ? (
            <>
              <Star className="w-3 h-3 fill-cn-terracotta-400 text-cn-terracotta-400" />
              {clinician.rating}
              <span className="text-cn-text-muted font-normal">({clinician.review_count})</span>
            </>
          ) : (
            <span className="text-cn-text-muted">New</span>
          )}
        </div>
        <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-xs font-medium">
          <span className={`w-1.5 h-1.5 rounded-full ${statusColor} animate-pulse`} />
          {clinician.availability_status === 'available' ? 'Available now' : clinician.availability_status}
        </div>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSave?.(clinician.id); }}
          data-testid={`save-clinician-${clinician.id}`}
          aria-pressed={!!isSaved}
          aria-label={isSaved ? 'Remove from shortlist' : 'Save to shortlist'}
          title={isSaved ? 'Saved' : 'Save'}
          className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 active:scale-95 transition-transform"
        >
          <Heart className={`w-4 h-4 transition-colors ${isSaved ? 'fill-cn-terracotta-500 text-cn-terracotta-500' : 'text-cn-text-muted'}`} />
        </button>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-semibold text-lg leading-snug">{clinician.name}</h3>
            <div className="text-sm text-cn-text-muted">{clinician.license_type} · {clinician.years_of_experience || 0} yrs</div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="font-display font-bold text-lg">${clinician.hourly_rate}<span className="text-xs text-cn-text-muted font-normal">/hr</span></div>
          </div>
        </div>
        {clinician.badges?.length > 0 && (
          <SpecialtyBadgeRow badges={clinician.badges} className="mt-2.5" />
        )}
        {clinician.specialties?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {clinician.specialties.slice(0, 3).map((s) => (
              <span key={s} className="text-[11px] bg-cn-bone-200 text-cn-text px-2 py-0.5 rounded-full">{s}</span>
            ))}
          </div>
        )}
        {clinician.accepts_insurance && (
          <div className="mt-2.5 inline-flex items-center gap-1 text-[11px] font-semibold text-cn-primary" data-testid={`card-insurance-${clinician.id}`}>
            <CreditCard className="w-3 h-3" /> Accepts insurance{clinician.insurance_verified ? ' · verified' : ''}
          </div>
        )}
        <div className="mt-auto pt-4 flex items-center justify-between text-xs text-cn-text-muted">
          {clinician.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {clinician.city}, {clinician.state}</span>}
          <div className="flex gap-1">
            {clinician.available_for_telehealth && <Video className="w-3.5 h-3.5" />}
            {clinician.available_for_in_person && <Home className="w-3.5 h-3.5" />}
          </div>
        </div>
      </div>
    </Link>
  );
}
